import { t } from "@/i18n";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Download, Printer, Sparkles } from "lucide-react";
import { API, api, formatApiError, printImage } from "@/lib/api";
import { Logo, Loader } from "@/components/Loader";

const TILTS = ["-rotate-1", "rotate-1", "rotate-0", "-rotate-2", "rotate-2"];

export default function SharedGallery() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/gallery/${token}`).then((r) => setData(r.data)).catch((e) => setError(formatApiError(e)));
  }, [token]);

  const img = (id) => `${API}/gallery/${token}/image/${id}`;

  return (
    <div className="min-h-[100dvh] bg-[#FFF9F2] dot-grid flex flex-col" data-testid="shared-gallery-page">
      <header className="h-20 px-4 sm:px-6 flex items-center justify-between bg-white/90 backdrop-blur-md border-b-2 border-slate-100">
        <Link to="/" data-testid="gallery-brand-link"><Logo small /></Link>
        <Link to="/signup" className="btn-pop btn-red !py-2.5 !px-5 text-sm" data-testid="gallery-cta-button"><Sparkles className="w-4 h-4" /> {t("make_your_own")}</Link>
      </header>
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 py-10">
        {error ? (
          <div className="card-soft p-10 text-center space-y-3 max-w-md mx-auto" data-testid="gallery-error">
            <p className="font-display text-2xl font-semibold">{t("oops")}</p>
            <p className="text-slate-600">{error}</p>
            <Link to="/" className="btn-pop btn-ink inline-flex">{t("go_to_paintipop")}</Link>
          </div>
        ) : !data ? (
          <Loader label={t("opening_the_gallery")} />
        ) : (
          <div className="space-y-10 rise">
            <div className="space-y-1">
              <p className="font-bold text-[#FF5252] uppercase tracking-wider text-sm">{t("the_art_gallery_of")}</p>
              <h1 className="text-4xl sm:text-5xl font-bold tracking-tight" data-testid="gallery-artist-name">{data.artist}</h1>
              <p className="text-slate-600 text-base sm:text-lg" data-testid="gallery-count">{data.items.length} coloured picture{data.items.length === 1 ? "" : "s"}</p>
            </div>
            {data.items.length === 0 ? (
              <p className="card-soft p-10 text-center text-slate-500" data-testid="gallery-empty">{t("no_pictures_yet_check_back_soon")}</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {data.items.map((a, i) => (
                  <figure key={a.id} className={`bg-white rounded-3xl border-4 border-[#1E293B] shadow-[6px_8px_0_0_#1E293B] p-3 ${TILTS[i % TILTS.length]} hover:rotate-0 transition-transform duration-300`} data-testid={`gallery-item-${a.id}`}>
                    <img src={img(a.id)} alt={a.page_title} loading="lazy" className="w-full aspect-square object-contain rounded-2xl" />
                    <figcaption className="flex items-center justify-between gap-2 px-1 pt-3">
                      <div className="min-w-0">
                        <p className="font-bold truncate">{a.page_title}</p>
                        <p className="text-xs text-slate-500">{new Date(a.created_at).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" })}</p>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <button onClick={() => printImage(img(a.id), `${a.page_title} by ${data.artist}`)} title={t("print")} data-testid={`gallery-print-${a.id}`}
                          className="p-2.5 rounded-xl border-2 border-slate-200 bg-white hover:bg-slate-50"><Printer className="w-4 h-4" /></button>
                        <a href={img(a.id)} download={`${a.page_title}.png`.replace(/\s+/g, "-").toLowerCase()} title={t("download")} data-testid={`gallery-download-${a.id}`}
                          className="p-2.5 rounded-xl border-2 border-slate-200 bg-white hover:bg-slate-50"><Download className="w-4 h-4" /></a>
                      </div>
                    </figcaption>
                  </figure>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
